import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Materials() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [materials, setMaterials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    color_code: '',
    material_type: 'granite',
    price_per_m2: '',
    budget_tier: 'mid',
    description: ''
  });

  useEffect(() => {
    fetchMaterials();
    fetchSettings();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/api/materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      toast.error('Failed to load materials');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/worktops/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load worktop settings:', error);
      // Use default settings if fetch fails
      setSettings({ retail_multiplier: 3.1 });
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Limit to 5 images
      const limitedFiles = files.slice(0, 5);
      setImageFiles([...imageFiles, ...limitedFiles]);
      
      // Create previews
      limitedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        color_code: formData.color_code,
        material_type: formData.material_type,
        price_per_m2: parseFloat(formData.price_per_m2),
        budget_tier: formData.budget_tier,
        description: formData.description,
        supplier: '',
        in_stock: true,
        images: imagePreviews // Send base64 images directly
      };

      if (editingMaterial) {
        // Preserve existing images if editing and no new images added
        if (imagePreviews.length === 0 && editingMaterial.images) {
          payload.images = editingMaterial.images;
        }
        
        // Update existing material
        await axios.put(`${API}/api/materials/${editingMaterial.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Material updated successfully');
      } else {
        // Create new material
        await axios.post(`${API}/api/materials`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('Material added successfully');
      }

      fetchMaterials();
      handleCloseDialog();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save material';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('Delete this material?')) return;

    try {
      await axios.delete(`${API}/api/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material deleted');
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      color_code: material.color_code || '',
      material_type: material.material_type || 'granite',
      price_per_m2: material.price_per_m2,
      budget_tier: material.budget_tier || 'mid',
      description: material.description || ''
    });
    // Set existing images as previews
    if (material.images && material.images.length > 0) {
      setImagePreviews(material.images.map(img => 
        img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`
      ));
    } else if (material.image_data) {
      // Fallback to legacy single image
      setImagePreviews([`data:image/jpeg;base64,${material.image_data}`]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMaterial(null);
    setImageFiles([]);
    setImagePreviews([]);
    setFormData({
      name: '',
      color_code: '',
      material_type: 'granite',
      price_per_m2: '',
      budget_tier: 'mid',
      description: ''
    });
  };

  const calculateRetailPrice = (costPrice) => {
    if (!settings || !costPrice) return costPrice;
    const multiplier = settings.retail_multiplier || 3.1;
    return (costPrice * multiplier).toFixed(2);
  };

  const getTierBadge = (tier) => {
    const badges = {
      'best-price': { label: 'Budget Friendly', color: 'bg-green-100 text-green-800' },
      'mid': { label: 'Mid Range', color: 'bg-blue-100 text-blue-800' },
      'premium': { label: 'Premium', color: 'bg-purple-100 text-purple-800' }
    };
    return badges[tier] || badges['mid'];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button 
          variant="outline" 
          onClick={() => navigate('/dashboard')} 
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Granite Colors & Materials</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Manage materials for instant online pricing</p>
            </div>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              Add Material
            </Button>
          </CardHeader>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm">Material Type</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="granite">Granite</SelectItem>
                    <SelectItem value="quartz">Quartz</SelectItem>
                    <SelectItem value="marble">Marble</SelectItem>
                    <SelectItem value="quartzite">Quartzite</SelectItem>
                    <SelectItem value="porcelain">Porcelain</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Budget Tier</Label>
                <Select value={filterTier} onValueChange={setFilterTier}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="best-price">Budget Friendly</SelectItem>
                    <SelectItem value="mid">Mid Range</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => { setFilterType('all'); setFilterTier('all'); }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {materials.filter(material => {
            if (filterType !== 'all' && material.material_type !== filterType) return false;
            if (filterTier !== 'all' && material.budget_tier !== filterTier) return false;
            return true;
          }).map((material) => {
            const tierInfo = getTierBadge(material.budget_tier);
            return (
              <Card key={material.id} className="overflow-hidden">
                {(material.images?.length > 0 || material.image_data) && (
                  <div className="h-48 overflow-hidden bg-gray-100 relative">
                    <img
                      src={material.images?.[0]?.startsWith('data:') ? material.images[0] : 
                           material.images?.[0] ? `data:image/jpeg;base64,${material.images[0]}` :
                           `data:image/jpeg;base64,${material.image_data}`}
                      alt={material.name}
                      className="w-full h-full object-cover"
                    />
                    {material.images?.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                        +{material.images.length - 1} more
                      </div>
                    )}
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{material.name}</h3>
                      <p className="text-xs text-gray-500 capitalize">{material.material_type || 'granite'}</p>
                      {material.color_code && (
                        <p className="text-sm text-gray-600">Code: {material.color_code}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${tierInfo.color}`}>
                      {tierInfo.label}
                    </span>
                  </div>
                  
                  <div className="bg-gray-50 rounded p-3 mb-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500">Cost Price</p>
                        <p className="text-lg font-bold text-gray-900">£{material.price_per_m2.toFixed(2)}/m²</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Retail Price</p>
                        <p className="text-lg font-bold text-blue-600">£{calculateRetailPrice(material.price_per_m2)}/m²</p>
                      </div>
                    </div>
                  </div>

                  {material.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{material.description}</p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(material)}
                      className="flex-1"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(material.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {materials.length === 0 && (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">No materials added yet</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              Add Your First Material
            </Button>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMaterial ? 'Edit Material' : 'Add New Material'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Black Granite"
                    required
                  />
                </div>
                
                <div>
                  <Label>Colour Code</Label>
                  <Input
                    value={formData.color_code}
                    onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                    placeholder="e.g., BG-001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Type *</Label>
                  <Select
                    value={formData.material_type}
                    onValueChange={(value) => setFormData({ ...formData, material_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="granite">Granite</SelectItem>
                      <SelectItem value="quartz">Quartz</SelectItem>
                      <SelectItem value="marble">Marble</SelectItem>
                      <SelectItem value="quartzite">Quartzite</SelectItem>
                      <SelectItem value="porcelain">Porcelain</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Cost Price per m² (£) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_per_m2}
                    onChange={(e) => setFormData({ ...formData, price_per_m2: e.target.value })}
                    placeholder="e.g., 250.00"
                    required
                  />
                  {settings && formData.price_per_m2 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Retail: £{calculateRetailPrice(parseFloat(formData.price_per_m2))}/m²
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Budget Tier *</Label>
                  <Select
                    value={formData.budget_tier}
                    onValueChange={(value) => setFormData({ ...formData, budget_tier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="best-price">Budget Friendly</SelectItem>
                      <SelectItem value="mid">Mid Range</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description or notes"
                  rows={3}
                />
              </div>

              <div>
                <Label>Upload Images (up to 5)</Label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Images will be automatically compressed. Max 5 images. Recommended: High-quality photos.
                </p>
                
                {imagePreviews.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Previews ({imagePreviews.length}):</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => { setImagePreviews([]); setImageFiles([]); }}
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={preview}
                            alt={`Preview ${idx + 1}`}
                            className="w-full h-32 object-cover rounded border"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => {
                              setImagePreviews(prev => prev.filter((_, i) => i !== idx));
                              setImageFiles(prev => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Saving...' : (editingMaterial ? 'Update Material' : 'Add Material')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Materials;
